import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields/TextInput";
import type { IndustryDetail, IndustryPayload } from "@/interface/masterManagement";
import { useCreateIndustry, useUpdateIndustry } from "../hooks/useIndustry";

const industrySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Industry Name is required")
    .max(255, "Industry Name cannot exceed 255 characters"),
});

export type IndustryFormValues = z.infer<typeof industrySchema>;

interface IndustryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: IndustryDetail | null;
}

export const IndustryFormModal: React.FC<IndustryFormModalProps> = ({
  isOpen,
  onClose,
  editData,
}) => {
  const createMutation = useCreateIndustry();
  const updateMutation = useUpdateIndustry();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IndustryFormValues>({
    resolver: zodResolver(industrySchema),
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          name: editData.industry_name || editData.name || "",
        });
      } else {
        reset({
          name: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: IndustryFormValues) => {
    try {
      const payload: IndustryPayload = {
        industry_name: values.name,
        name: values.name,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handled by query
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Industry" : "Add Industry"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="name"
              label="Industry Name"
              placeholder="e.g. Financial Technology"
              required
            />
          </div>

          <DialogFooter className="p-6 border-t border-slate-100 dark:border-slate-800">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
            >
              {isSubmitting || createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : editData
                ? "Save Changes"
                : "Add Industry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default IndustryFormModal;
